import React, { useState, useEffect } from 'react';
import StatsBar from './StatsBar';
import Chart from './Chart';
import PredictionButtons from './PredictionButtons';

function MarketCard({ 
  coinName,
  currentPrice,
  priceChange,
  watchlistCount,
  participantCount,
  predictions = {
    '10m': { up: '$1.2K', down: '$0.8K' },
    '1h': { up: '$3.4K', down: '$2.1K' },
    '1d': { up: '$8.9K', down: '$5.6K' }
  },
  totalVolume,
  trend = 'neutral',
  tags = [],
  isWatchlisted = false,
  chartData = [],
  globalBettingOverlay,
  onGlobalAreaClick,
  onCloseGlobalOverlay
}) {
  const [watchlisted, setWatchlisted] = useState(isWatchlisted);
  const [message, setMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);

  // 模拟1小时走势数据（Recharts格式）
  const generateChartData = () => {
    const data = [];
    const basePrice = parseFloat(currentPrice?.toString().replace(/,/g, '') || '50000');
    let volatility = 0.12;
    if (basePrice < 1) {
      volatility = 0.25;
    } else if (basePrice < 100) {
      volatility = 0.18;
    } else if (basePrice > 10000) {
      volatility = 0.08;
    }
    let pricePoint = basePrice;
    for (let i = 0; i < 120; i++) {
      const change = (Math.random() - 0.5) * volatility;
      pricePoint = pricePoint * (1 + change);
      const maxDeviation = basePrice * 0.2;
      if (Math.abs(pricePoint - basePrice) > maxDeviation) {
        pricePoint = basePrice + (pricePoint > basePrice ? maxDeviation : -maxDeviation);
      }
      data.push({
        time: i,
        price: pricePoint,
        volume: Math.floor(Math.random() * 10+1)
      });
    }
    return data;
  };

  // 转换真实图表数据格式以匹配Chart组件期望的格式
  const convertChartData = (apiData) => {
    if (!apiData || apiData.length === 0) return [];
    
    // console.log(`📊 [${coinName}] 转换图表数据:`, apiData.length, '个数据点');
    // console.log(`📊 [${coinName}] 原始数据示例:`, apiData[0]);
    
    // 将数据映射到0-360的时间范围
    const totalPoints = apiData.length;
    const timeRange = 360;
    
    const converted = apiData.map((item, index) => ({
      time: Math.floor((index / totalPoints) * timeRange), // 映射到0-360范围
      price: parseFloat(item.price),
      volume: Math.floor(Math.random() * 10 + 1) // API数据没有volume，生成随机值
    }));
    
    // console.log(`📊 [${coinName}] 转换后数据示例:`, converted[0], '最后:', converted[converted.length - 1]);
    return converted;
  };

  // 使用真实图表数据，如果没有则生成fallback数据
  const chartDataForSymbol = chartData || [];
  
  const finalChartData = chartDataForSymbol.length > 0 ? convertChartData(chartDataForSymbol) : generateChartData();
  
  // 只在数据第一次加载时打印最终传给图表的数据
  useEffect(() => {
    if (finalChartData.length > 0) {
      // console.log(`📊 [${coinName}] 最终传给图表的数据 (${finalChartData.length}个点):`, finalChartData);
    }
  }, [finalChartData.length, coinName]);

  // 新增：保存最新模拟现价
  const [currentSimPrice, setCurrentSimPrice] = useState(finalChartData[finalChartData.length - 1]?.price);

  // 处理点击区域事件 - 使用全局蒙版
  const handleAreaClick = (symbol, area) => {
    const lastPrice = finalChartData[finalChartData.length - 1]?.price || 0;
    // 统一点击事件，默认为看涨方向
    const direction = area === 'unified' ? 'green' : area;
    onGlobalAreaClick(symbol || coinName, direction, lastPrice);
  };

  return (
    <div className="relative rounded-lg bg-white/5 p-4 hover:bg-white/10 transition-all duration-300 border border-white/10 hover:border-white/20 hover:shadow-lg hover:shadow-white/5 cursor-pointer">
      
      {/* 消息提示 */}
      {showMessage && (
        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 z-50 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg border border-gray-600 animate-pulse">
          <div className="text-sm font-medium">{message}</div>
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
        </div>
      )}
      
      {/* 第一行：收藏+币名+现价+涨幅 vs 关注+参与人数 */}
      <StatsBar
        coinName={coinName}
        currentPrice={currentSimPrice}
        priceChange={priceChange}
        tags={tags}
        watchlistCount={watchlistCount}
        participantCount={participantCount}
        watchlisted={watchlisted}
        onToggleWatchlist={() => setWatchlisted(!watchlisted)}
      />
      
      {/* 第二行：走势图与预测 */}
      <div className="flex items-center bg-white/5 rounded-lg focus:outline-none [&_*]:focus:outline-none [&_*]:outline-none overflow-hidden min-h-[220px] h-[220px]">
        <Chart 
          chartData={finalChartData} 
          priceChange={priceChange} 
          symbol={coinName}
          onAreaClick={handleAreaClick}
          showBettingOverlay={globalBettingOverlay.show && globalBettingOverlay.symbol === coinName}
          bettingData={globalBettingOverlay}
          onCloseBettingOverlay={onCloseGlobalOverlay}
          onPriceUpdate={setCurrentSimPrice}
        />
      </div>
    </div>
  );
}

export default MarketCard;
